// Гость → аккаунт на витрине (правило заказчика 25.09.2026).
//
// Не вошедший покупатель может всё: смотреть, лайкать, класть в корзину. Его
// лайки, корзина и просмотры привязаны к УСТРОЙСТВУ: clientId в избранном,
// cartId гостевой корзины на сервере, просмотры — события витрины по clientId.
//
// - Вошёл/зарегистрировался (или уже вошедший открыл витрину, или оформляет
//   заказ) → `attachDeviceToAccount`: сервер сливает накопленное на устройстве
//   с аккаунтом без дублей, устройство переключается на корзину аккаунта.
// - Вышел → `resetDevice`: на устройстве не остаётся ничего от аккаунта, это
//   новый гость с пустыми данными.
// - Гостевой кэш устройства старше срока давности сбрасывается
//   (`expireStaleGuestDevice`); сервер чистит гостевые корзины тем же сроком.
import { api } from "@/api";
import { getBuyerAuthToken } from "@/api/token";
import { useCart } from "./cart";
import { likeWriteMark, useWishlist } from "./wishlist";

/** Срок давности гостевых данных без активности. Сервер держит тот же срок в
 *  GUEST_DATA_TTL_DAYS (по умолчанию 30) для гостевых корзин и лайков. */
export const GUEST_DATA_TTL_DAYS = 30;
const DAY_MS = 86_400_000;

let inFlight: Promise<void> | null = null;

/**
 * Присоединить устройство к аккаунту. Идемпотентно и без ошибок наружу: вход
 * не должен падать из-за того, что слияние не удалось, — повторится на
 * следующем открытии витрины. Параллельные вызовы схлопываются в один.
 */
export const attachDeviceToAccount = (): Promise<void> => {
	if (!getBuyerAuthToken()) return Promise.resolve();
	if (inFlight) return inFlight;
	inFlight = (async () => {
		const mark = likeWriteMark();
		try {
			const result = await api.buyer.attachDevice({
				clientId: useWishlist.getState().clientId,
				cartId: useCart.getState().cartId,
			});
			// Токен успели сбросить (вышел, пока шёл запрос) — ответ аккаунта на
			// устройство не кладём.
			if (!getBuyerAuthToken()) return;
			useCart.getState().adopt(result.cart);
			useWishlist.getState().applyServerStateSince(mark, result.likes);
		} catch {
			// офлайн/ошибка — остаёмся на корзине устройства и повторим слияние
			// при следующем открытии витрины.
			await useCart.getState().refresh();
		} finally {
			inFlight = null;
		}
	})();
	return inFlight;
};

/** Выход из аккаунта: устройство становится новым гостем с пустыми данными. */
export const resetDevice = (): void => {
	useCart.getState().clear();
	useWishlist.getState().resetDevice();
};

/**
 * Гостевой кэш устройства, которое не открывали дольше срока, — сбросить.
 * Вошедшего не трогаем: его данные живут в аккаунте. Возвращает true, если
 * сбросили. Каждое открытие витрины продлевает срок.
 */
export const expireStaleGuestDevice = (now = Date.now()): boolean => {
	const { lastSeenAt, touch } = useWishlist.getState();
	const stale = !getBuyerAuthToken() && typeof lastSeenAt === "number" && now - lastSeenAt > GUEST_DATA_TTL_DAYS * DAY_MS;
	if (stale) resetDevice();
	else touch();
	return stale;
};
